package com.rainbowclubs.rainbow.data.repositories

import com.rainbowclubs.rainbow.data.models.Membership
import com.rainbowclubs.rainbow.data.models.MembershipRequest
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.data.models.MembershipsPageResponse
import com.rainbowclubs.rainbow.data.requests.UpdateMembershipRequest
import com.rainbowclubs.rainbow.data.requests.UpdateMembershipStatusRequest
import com.rainbowclubs.rainbow.domain.repositories.MembershipRepository
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*

class MembershipRepositoryImpl(
    private val httpClient: HttpClient,
) : MembershipRepository {
    override suspend fun getExistingMemberships(
        clubId: String,
        page: Int,
        limit: Int,
        search: String?,
    ): Result<MembershipsPageResponse> {
        return try {
            val response = httpClient.get("/memberships/$clubId") {
                parameter("page", page)
                parameter("limit", limit)
                search?.takeIf { it.isNotBlank() }?.let {
                    parameter("search", it)
                }
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getPendingMemberships(clubId: String): Result<List<Membership>> {
        return try {
            val response = httpClient.get("/memberships/pending/$clubId")
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getMembershipById(membershipId: String): Result<Membership> {
        return try {
            val response = httpClient.get("/membership/$membershipId")
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createMembership(request: MembershipRequest): Result<Boolean> {
        return try {
            val response = httpClient.post("/membership") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                Result.success(true)
            } else {
                Result.failure(Exception("Failed to create membership: ${response.status.description}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateMembershipStatus(
        membershipId: String,
        status: MembershipStatus,
    ): Result<Membership> {
        return try {
            val response = httpClient.patch("/membership/$membershipId/status") {
                contentType(ContentType.Application.Json)
                setBody(UpdateMembershipStatusRequest(status))
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateMembership(
        membershipId: String,
        request: UpdateMembershipRequest,
    ): Result<Membership> {
        return try {
            val response = httpClient.patch("/membership/$membershipId") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }
            if (response.status.isSuccess()) {
                Result.success(response.body())
            } else {
                Result.failure(Exception(response.status.description))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
